
import React, { Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import ProtectedRoute from '@/components/security/ProtectedRoute';
import SubscriptionGatedRoute from '@/components/security/SubscriptionGatedRoute';
import {
  AuthErrorBoundary,
  DashboardErrorBoundary,
  PaymentErrorBoundary,
  SessionErrorBoundary,
} from '@/components/error-boundary/SentryErrorBoundary';

// PERFORMANCE FIX: Implement code splitting for all route components
const SalesLanding = React.lazy(() => import('@/pages/SalesLanding'));
const Auth = React.lazy(() => import('@/pages/Auth'));
const Subscription = React.lazy(() => import('@/pages/Subscription'));
const SubscriptionSuccess = React.lazy(() => import('@/pages/subscription/Success'));
const Unsubscribe = React.lazy(() => import('@/pages/Unsubscribe'));
const ProductSpecification = React.lazy(() => import('@/pages/ProductSpecification'));
const GDPRPolicy = React.lazy(() => import('@/pages/GDPRPolicy'));
const PrivacyPolicy = React.lazy(() => import('@/pages/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('@/pages/TermsOfService'));
const Dashboard = React.lazy(() => import('@/pages/Dashboard'));
const Account = React.lazy(() => import('@/pages/Account'));
const PersonaLibrary = React.lazy(() => import('@/pages/PersonaLibrary'));
const Requests = React.lazy(() => import('@/pages/Requests'));
const SessionSetup = React.lazy(() => import('@/pages/SessionSetup'));
const SessionChat = React.lazy(() => import('@/pages/SessionChat'));
const MentorFeedbackView = React.lazy(() => import('@/pages/MentorFeedbackView'));
const SessionReflection = React.lazy(() => import('@/pages/SessionReflection'));
const SessionDetails = React.lazy(() => import('@/pages/SessionDetails'));
const CoachProgress = React.lazy(() => import('@/pages/CoachProgress'));
const TechniqueAnalysis = React.lazy(() => import('@/pages/TechniqueAnalysis'));
const MentorFeedbacks = React.lazy(() => import('@/pages/MentorFeedbacks'));
const NotFound = React.lazy(() => import('@/pages/NotFound'));

// Loading fallback component
const RouteLoadingFallback = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
      <p className="text-muted-foreground">Loading...</p>
    </div>
  </div>
);

// CS-109: each guarded route's element is wrapped in its section's error
// boundary (inside the guard, around Suspense so chunk-load failures are
// caught too). A render error now shows a recoverable card for that route
// instead of tripping the app-wide boundary in App.tsx, which replaces the
// router, providers and toaster.
const AppRoutes = () => (
  <Routes>
    {/* Public routes */}
    <Route path="/" element={
      <Suspense fallback={<RouteLoadingFallback />}>
        <SalesLanding />
      </Suspense>
    } />
    <Route path="/auth" element={
      <AuthErrorBoundary>
        <Suspense fallback={<RouteLoadingFallback />}>
          <Auth />
        </Suspense>
      </AuthErrorBoundary>
    } />
    <Route path="/subscription" element={
      <PaymentErrorBoundary>
        <Suspense fallback={<RouteLoadingFallback />}>
          <Subscription />
        </Suspense>
      </PaymentErrorBoundary>
    } />
    <Route path="/subscription/success" element={
      <PaymentErrorBoundary>
        <Suspense fallback={<RouteLoadingFallback />}>
          <SubscriptionSuccess />
        </Suspense>
      </PaymentErrorBoundary>
    } />
    <Route path="/unsubscribe" element={
      <Suspense fallback={<RouteLoadingFallback />}>
        <Unsubscribe />
      </Suspense>
    } />
    <Route path="/product-specification" element={
      <Suspense fallback={<RouteLoadingFallback />}>
        <ProductSpecification />
      </Suspense>
    } />
    <Route path="/gdpr-policy" element={
      <Suspense fallback={<RouteLoadingFallback />}>
        <GDPRPolicy />
      </Suspense>
    } />
    <Route path="/privacy-policy" element={
      <Suspense fallback={<RouteLoadingFallback />}>
        <PrivacyPolicy />
      </Suspense>
    } />
    <Route path="/terms-of-service" element={
      <Suspense fallback={<RouteLoadingFallback />}>
        <TermsOfService />
      </Suspense>
    } />
    
    {/* Protected routes - require authentication */}
    <Route path="/dashboard" element={
      <ProtectedRoute>
        <DashboardErrorBoundary>
          <Suspense fallback={<RouteLoadingFallback />}>
            <Dashboard />
          </Suspense>
        </DashboardErrorBoundary>
      </ProtectedRoute>
    } />
    
    <Route path="/account" element={
      <ProtectedRoute>
        <PaymentErrorBoundary>
          <Suspense fallback={<RouteLoadingFallback />}>
            <Account />
          </Suspense>
        </PaymentErrorBoundary>
      </ProtectedRoute>
    } />
    
    <Route path="/personas" element={
      <ProtectedRoute>
        <DashboardErrorBoundary>
          <Suspense fallback={<RouteLoadingFallback />}>
            <PersonaLibrary />
          </Suspense>
        </DashboardErrorBoundary>
      </ProtectedRoute>
    } />
    
    <Route path="/requests" element={
      <ProtectedRoute>
        <DashboardErrorBoundary>
          <Suspense fallback={<RouteLoadingFallback />}>
            <Requests />
          </Suspense>
        </DashboardErrorBoundary>
      </ProtectedRoute>
    } />
    
    <Route path="/session/new" element={
      <ProtectedRoute>
        <SessionErrorBoundary>
          <Suspense fallback={<RouteLoadingFallback />}>
            <SessionSetup />
          </Suspense>
        </SessionErrorBoundary>
      </ProtectedRoute>
    } />
    
    <Route path="/session/:threadId" element={
      <ProtectedRoute>
        <SessionErrorBoundary>
          <Suspense fallback={<RouteLoadingFallback />}>
            <SessionChat />
          </Suspense>
        </SessionErrorBoundary>
      </ProtectedRoute>
    } />
    
    <Route path="/session/:threadId/mentor-feedback" element={
      <ProtectedRoute>
        <SessionErrorBoundary>
          <Suspense fallback={<RouteLoadingFallback />}>
            <MentorFeedbackView />
          </Suspense>
        </SessionErrorBoundary>
      </ProtectedRoute>
    } />
    
    <Route path="/session/:threadId/reflection" element={
      <ProtectedRoute>
        <SessionErrorBoundary>
          <Suspense fallback={<RouteLoadingFallback />}>
            <SessionReflection />
          </Suspense>
        </SessionErrorBoundary>
      </ProtectedRoute>
    } />
    
    <Route path="/sessions/:sessionId" element={
      <ProtectedRoute>
        <SessionErrorBoundary>
          <Suspense fallback={<RouteLoadingFallback />}>
            <SessionDetails />
          </Suspense>
        </SessionErrorBoundary>
      </ProtectedRoute>
    } />
    
    {/* Alternative route for session details - compatibility */}
    <Route path="/session-details/:id" element={
      <ProtectedRoute>
        <SessionErrorBoundary>
          <Suspense fallback={<RouteLoadingFallback />}>
            <SessionDetails />
          </Suspense>
        </SessionErrorBoundary>
      </ProtectedRoute>
    } />
    
    {/* Premium feature routes with subscription gating */}
    <Route path="/mentor-feedbacks" element={
      <SubscriptionGatedRoute
        requiredFeature="mentor_feedback"
        featureName="Mentor Feedback"
        description="Access detailed feedback from AI mentors to improve your coaching skills."
      >
        <DashboardErrorBoundary>
          <Suspense fallback={<RouteLoadingFallback />}>
            <MentorFeedbacks />
          </Suspense>
        </DashboardErrorBoundary>
      </SubscriptionGatedRoute>
    } />
    
    <Route path="/coach-progress" element={
      <SubscriptionGatedRoute
        requiredFeature="coach_progress"
        featureName="Coach Progress"
        description="Track your coaching development and skill progression over time."
      >
        <DashboardErrorBoundary>
          <Suspense fallback={<RouteLoadingFallback />}>
            <CoachProgress />
          </Suspense>
        </DashboardErrorBoundary>
      </SubscriptionGatedRoute>
    } />
    
    <Route path="/technique-analysis" element={
      <SubscriptionGatedRoute
        requiredFeature="technique_analysis"
        featureName="Technique Analysis"
        description="Advanced analysis of your coaching techniques and communication patterns."
      >
        <DashboardErrorBoundary>
          <Suspense fallback={<RouteLoadingFallback />}>
            <TechniqueAnalysis />
          </Suspense>
        </DashboardErrorBoundary>
      </SubscriptionGatedRoute>
    } />
    
    {/* Catch-all route for 404 */}
    <Route path="*" element={
      <Suspense fallback={<RouteLoadingFallback />}>
        <NotFound />
      </Suspense>
    } />
  </Routes>
);

export default AppRoutes;
